/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * CampaignInfo selectors
 *
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.12.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../reducers/rootReducer';
import { campaignInfoInitialState } from './campaignInfoConstant';
import { ICampaignInfoReducer } from './campaignInfoReducer';
import { ECampaignInfoActions, ICampaignInfoActivateResult, ICampaignInfoGeneral } from './types/campaignInfoTypes';

export const makeSelectCampaignInfo = (state: IRootReducer): ICampaignInfoReducer =>
    state.campaignInfo || campaignInfoInitialState;

/*
* -------------------------------------------
*           General info
* -------------------------------------------
*/

export const makeSelectCampaignInfoGeneral = ():
    OutputSelector<IRootReducer, ICampaignInfoGeneral, (state: ICampaignInfoReducer) => ICampaignInfoGeneral> =>
    createSelector(makeSelectCampaignInfo, (state) => state.info);

/*
* -------------------------------------------
*          Max subs available for activation
* -------------------------------------------
*/

export const makeSelectCampaignMaxParticipantsForActivation = ():
    OutputSelector<IRootReducer, number, (state: ICampaignInfoReducer) => number> =>
    createSelector(makeSelectCampaignInfo, (state) => state.maxParticipantsForActivation || 0);

/*
* -------------------------------------------
*           Page statuses
* -------------------------------------------
*/

export const makeSelectCampaignInfoIsLoading = ():
    OutputSelector<IRootReducer, boolean, (state: ICampaignInfoReducer) => boolean> =>
    createSelector(makeSelectCampaignInfo, (state) => state.isLoading);

export const makeSelectCampaignInfoIsError = ():
    OutputSelector<IRootReducer, boolean, (state: ICampaignInfoReducer) => boolean> =>
    createSelector(makeSelectCampaignInfo, (state) => state.isError);

/*
* -------------------------------------------
*           Actions
* -------------------------------------------
*/


export const makeSelectCampaignInfoActions = ():
    OutputSelector<IRootReducer, ECampaignInfoActions[], (state: ICampaignInfoReducer) => ECampaignInfoActions[]> =>
    createSelector(makeSelectCampaignInfo, (state) => state.actions);

/*
* -------------------------------------------
*           Modal data
* -------------------------------------------
*/

export const makeSelectCampaignInfoActivateResult = ():
    OutputSelector<IRootReducer, ICampaignInfoActivateResult,
        (state: ICampaignInfoReducer) => ICampaignInfoActivateResult> =>
    createSelector(makeSelectCampaignInfo, (state) => state.activateResult);

